import { useEffect, useState } from 'react'
import { Show } from '@refinedev/antd'
import { useShow, useUpdate } from '@refinedev/core'
import {
  Descriptions,
  Table,
  Card,
  Tag,
  Select,
  Input,
  Button,
  Space,
  Row,
  Col,
  message,
} from 'antd'

const ORDER_STATUSES = ['PENDING', 'CONFIRMED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED']
const PAYMENT_STATUSES = ['PENDING', 'PAID', 'FAILED', 'REFUNDED']
const SHIPPING_STATUSES = ['PENDING', 'SHIPPED', 'IN_TRANSIT', 'DELIVERED']

export default function OrderShowPage() {
  const { query } = useShow()
  const record = query?.data?.data as any
  const isLoading = query?.isLoading
  const { mutate } = useUpdate()

  const [isSaving, setIsSaving] = useState(false)
  const [status, setStatus] = useState<string>()
  const [paymentStatus, setPaymentStatus] = useState<string>()
  const [shippingStatus, setShippingStatus] = useState<string>()
  const [trackingNumber, setTrackingNumber] = useState<string>('')

  useEffect(() => {
    if (record) {
      setStatus(record.status)
      setPaymentStatus(record.paymentStatus)
      setShippingStatus(record.shippingStatus ?? undefined)
      setTrackingNumber(record.trackingNumber ?? '')
    }
  }, [record])

  const save = () => {
    setIsSaving(true)
    mutate(
      {
        resource: 'orders',
        id: record.id,
        values: { status, paymentStatus, shippingStatus, trackingNumber },
      },
      {
        onSuccess: () => message.success('Porudžbina ažurirana'),
        onError: () => message.error('Ažuriranje nije uspelo'),
        onSettled: () => setIsSaving(false),
      },
    )
  }

  const addr = record?.shippingAddress ?? {}

  return (
    <Show isLoading={isLoading} title={record ? `Porudžbina ${record.orderNumber}` : 'Porudžbina'}>
      {record && (
        <Row gutter={16}>
          <Col xs={24} lg={15}>
            <Card title="Detalji" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Broj">{record.orderNumber}</Descriptions.Item>
                <Descriptions.Item label="Datum">
                  {new Date(record.createdAt).toLocaleString('sr-RS')}
                </Descriptions.Item>
                <Descriptions.Item label="Kupac">{record.customerName}</Descriptions.Item>
                <Descriptions.Item label="Email">{record.customerEmail ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Način plaćanja">{record.paymentMethod ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Ukupno">
                  {Number(record.total).toLocaleString('sr-RS')} {record.currency}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title="Stavke" style={{ marginBottom: 16 }}>
              <Table dataSource={record.items} rowKey="id" pagination={false} size="small">
                <Table.Column title="Proizvod" dataIndex="productName" />
                <Table.Column title="Količina" dataIndex="quantity" width={100} />
                <Table.Column
                  title="Cena"
                  dataIndex="price"
                  render={(p: number) => `${Number(p).toLocaleString('sr-RS')} ${record.currency}`}
                />
                <Table.Column
                  title="Ukupno"
                  render={(_: unknown, r: any) =>
                    `${(Number(r.price) * r.quantity).toLocaleString('sr-RS')} ${record.currency}`
                  }
                />
              </Table>
            </Card>

            <Card title="Dostava">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Primalac">{addr.fullName ?? '—'}</Descriptions.Item>
                <Descriptions.Item label="Adresa">
                  {[addr.address, addr.city, addr.postalCode].filter(Boolean).join(', ') || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Telefon">{addr.phone ?? '—'}</Descriptions.Item>
                {addr.note && <Descriptions.Item label="Napomena">{addr.note}</Descriptions.Item>}
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card title="Upravljanje">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <div>
                  <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Status porudžbine</div>
                  <Select value={status} onChange={setStatus} style={{ width: '100%' }}>
                    {ORDER_STATUSES.map((s) => (
                      <Select.Option key={s} value={s}>
                        {s}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Status plaćanja</div>
                  <Select value={paymentStatus} onChange={setPaymentStatus} style={{ width: '100%' }}>
                    {PAYMENT_STATUSES.map((s) => (
                      <Select.Option key={s} value={s}>
                        {s}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Status isporuke</div>
                  <Select
                    value={shippingStatus}
                    onChange={setShippingStatus}
                    allowClear
                    placeholder="—"
                    style={{ width: '100%' }}
                  >
                    {SHIPPING_STATUSES.map((s) => (
                      <Select.Option key={s} value={s}>
                        {s}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <div style={{ marginBottom: 4, color: '#8c8c8c' }}>Broj za praćenje</div>
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="npr. RR123456789RS"
                  />
                </div>
                <Button type="primary" block loading={isSaving} onClick={save}>
                  Sačuvaj
                </Button>
              </Space>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <Space>
                <span>Trenutno:</span>
                <Tag color="blue">{record.status}</Tag>
                <Tag color={record.paymentStatus === 'PAID' ? 'green' : 'gold'}>
                  {record.paymentStatus}
                </Tag>
              </Space>
            </Card>
          </Col>
        </Row>
      )}
    </Show>
  )
}
